import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Card,
    Button,
    Upload,
    Table,
    Typography,
    Space,
    message,
    Spin,
    Alert,
    Modal,
    Progress,
    Tag,
    Divider,
    Row,
    Col,
    Select,
    Form,
    Tooltip,
    Result
} from 'antd';
import {
    UploadOutlined,
    DownloadOutlined,
    FileTextOutlined,
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    ArrowLeftOutlined,
    DeleteOutlined
} from '@ant-design/icons';
// Removed react-csv import - using custom download function instead
import auctionService from '../../services/auctionService';
import DashboardLayout from '../Dashboard/DashboardLayout';
import PageHeader from '../Common/PageHeader';
import { useSelector } from 'react-redux';

const { Title, Paragraph } = Typography;
const { Option } = Select;

// Helper function to format price
const formatPrice = (price) => {
    if (price === null || price === undefined) return '0.00';
    return Number(price).toFixed();
};

// Helper function to format currency with PKR
const formatCurrency = (price) => {
    return `PKR ${formatPrice(price)}`;
};

const BulkUploadLots = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [auctions, setAuctions] = useState([]);
    const [selectedAuction, setSelectedAuction] = useState(null);
    const [csvData, setCsvData] = useState([]);
    const [validationErrors, setValidationErrors] = useState([]);
    const [uploadResults, setUploadResults] = useState(null);
    const [showResults, setShowResults] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { currentUser } = useSelector((state) => state.user);
    
    // Watch the auction_id field value from the form
    const auctionIdValue = Form.useWatch('auction_id', form);

    useEffect(() => {
        if (currentUser?.has_auctions) {
            loadAuctions();
        }
    }, [currentUser]);

    // Effect to set the auction from query params after auctions are loaded
    useEffect(() => {
        const auctionSlug = searchParams.get('auction_slug');
        if (auctionSlug && auctions.length > 0) {
            const auction = auctions.find(a => a.slug === auctionSlug);
            if (auction) {
                // Only set if not already set or if it's a different auction
                if (!selectedAuction || selectedAuction.id !== auction.id) {
                    setSelectedAuction(auction);
                    // Use setTimeout to ensure form and Select are ready
                    setTimeout(() => {
                        form.setFieldsValue({ auction_id: auction.id });
                    }, 100);
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auctions, searchParams]);

    const loadAuctions = async () => {
        try {
            setLoading(true);
            const response = await auctionService.getAuctionsForDropdownActive();
            if (response?.data) {
                setAuctions(response.data);
            }
        } catch (error) {
            console.error('Error loading auctions:', error);
            message.error('Failed to load auctions');
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = (info) => {
        const { file } = info;
        
        if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
            message.error('Please upload a CSV file');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const csvText = e.target.result;
                const lines = csvText.split('\n').filter(line => line.trim());
                
                if (lines.length < 2) {
                    message.error('CSV file must have at least a header row and one data row');
                    return;
                }

                const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
                const data = [];

                for (let i = 1; i < lines.length; i++) {
                    const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
                    if (values.length === headers.length) {
                        const row = {};
                        headers.forEach((header, index) => {
                            row[header] = values[index];
                        });
                        data.push(row);
                    }
                }

                if (data.length === 0) {
                    message.error('No valid data rows found in CSV file');
                    return;
                }

                setCsvData(data);
                validateData(data);
                message.success(`Loaded ${data.length} lots from CSV file`);
            } catch (error) {
                console.error('Error parsing CSV:', error);
                message.error('Error parsing CSV file');
            }
        };
        reader.readAsText(file);
    };

    const validateData = (data) => {
        const errors = [];
        const requiredFields = ['title', 'description', 'starting_price', 'quantity'];
        
        data.forEach((row, index) => {
            const rowErrors = [];
            const rowNumber = index + 1;

            // Check required fields
            requiredFields.forEach(field => {
                if (!row[field] || row[field].trim() === '') {
                    rowErrors.push(`${field} is required`);
                }
            });

            // Validate starting price
            if (row.starting_price && isNaN(parseFloat(row.starting_price))) {
                rowErrors.push('starting_price must be a valid number');
            } else if (row.starting_price && parseFloat(row.starting_price) < 0) {
                rowErrors.push('starting_price must be greater than or equal to 0');
            }

            // Validate reserve price
            if (row.reserve_price && isNaN(parseFloat(row.reserve_price))) {
                rowErrors.push('reserve_price must be a valid number');
            } else if (row.reserve_price && parseFloat(row.reserve_price) < 0) {
                rowErrors.push('reserve_price must be greater than or equal to 0');
            }

            // Validate starting price vs reserve price
            if (row.starting_price && row.reserve_price) {
                const startingPrice = parseFloat(row.starting_price);
                const reservePrice = parseFloat(row.reserve_price);
                if (startingPrice > reservePrice) {
                    rowErrors.push('starting_price cannot be greater than reserve_price');
                }
            }

            // Validate quantity
            if (row.quantity && (isNaN(parseInt(row.quantity)) || parseInt(row.quantity) < 1)) {
                rowErrors.push('quantity must be a positive integer');
            }

            // Validate default_increment
            if (row.default_increment && (isNaN(parseInt(row.default_increment)) || parseInt(row.default_increment) < 1)) {
                rowErrors.push('default_increment must be a positive integer (minimum 1)');
            }

            if (rowErrors.length > 0) {
                errors.push({
                    row: rowNumber,
                    errors: rowErrors
                });
            }
        });

        setValidationErrors(errors);
    };

    const handleUpload = async () => {
        if (!selectedAuction) {
            message.error('Please select an auction');
            return;
        }

        if (csvData.length === 0) {
            message.error('Please upload a CSV file first');
            return;
        }

        if (validationErrors.length > 0) {
            message.error('Please fix validation errors before uploading');
            return;
        }

        try {
            setUploading(true);
            const response = await auctionService.bulkUploadLots(selectedAuction.slug, csvData);
            
            setUploadResults(response);
            setShowResults(true);
            message.success(`Successfully created ${response.success_count} lots`);
            
            // Clear CSV data and validation errors, but preserve auction selection
            setCsvData([]);
            setValidationErrors([]);
            // Don't reset the form - preserve the auction selection for "Upload More Lots"
        } catch (error) {
            console.error('Error uploading lots:', error);
            if (error.response?.data?.errors) {
                setValidationErrors(error.response.data.errors);
                message.error('Some lots failed validation. Please check the errors below.');
            } else {
                message.error(error.response?.data?.message || 'Failed to upload lots');
            }
        } finally {
            setUploading(false);
        }
    };

    const downloadTemplate = () => {
        const templateData = [
            {
                title: 'Sample Lot 1',
                description: 'This is a sample lot description',
                starting_price: '100.00',
                reserve_price: '150.00',
                quantity: '1',
                default_increment: '10',
                video_url: 'https://example.com/video1',
                specifications: 'Physical:Material:Wood|Physical:Color:Brown|Physical:Size:Large|Technical:Processor:Intel i7|Warranty:Period:2 Years'
            },
            {
                title: 'Sample Lot 2',
                description: 'Another sample lot description',
                starting_price: '200.00',
                reserve_price: '',
                quantity: '2',
                default_increment: '25',
                video_url: '',
                specifications: 'Electronics:Brand:Apple|Electronics:Model:iPhone 13|Electronics:Condition:New|Accessories:Charger:Included|Accessories:Case:Leather'
            },
            {
                title: 'Sample Lot 3',
                description: 'Simple format example',
                starting_price: '50.00',
                reserve_price: '',
                quantity: '1',
                default_increment: '5',
                video_url: '',
                specifications: 'Brand:Samsung|Model:Galaxy S21|Color:Black|Storage:128GB'
            }
        ];

        // Convert to CSV format
        const headers = Object.keys(templateData[0]);
        const csvContent = [
            headers.join(','),
            ...templateData.map(row => 
                headers.map(header => `"${row[header] || ''}"`).join(',')
            )
        ].join('\n');

        // Create and download file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'lot-upload-template.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const csvColumns = [
        {
            title: 'Row',
            dataIndex: 'row',
            key: 'row',
            width: 60,
            render: (_, __, index) => index + 1
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            width: 200,
            render: (text) => text || '-'
        },
        {
            title: 'Starting Price',
            dataIndex: 'starting_price',
            key: 'starting_price',
            width: 120,
            render: (price) => price ? formatCurrency(price) : '-'
        },
        {
            title: 'Reserve Price',
            dataIndex: 'reserve_price',
            key: 'reserve_price',
            width: 120,
            render: (price) => price ? formatCurrency(price) : 'No reserve'
        },
        {
            title: 'Quantity',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 80,
            render: (qty) => qty || '1'
        },
        {
            title: 'Price Increment',
            dataIndex: 'default_increment',
            key: 'default_increment',
            width: 120,
            render: (increment) => increment ? formatCurrency(increment) : 'PKR 5 (default)'
        },
        {
            title: 'Status',
            key: 'status',
            width: 100,
            render: (_, __, index) => {
                const hasError = validationErrors.some(error => error.row === index + 1);
                return hasError ? (
                    <Tag color="red">Error</Tag>
                ) : (
                    <Tag color="green">Valid</Tag>
                );
            }
        }
    ];

    const errorColumns = [
        {
            title: 'Row',
            dataIndex: 'row',
            key: 'row',
            width: 60
        },
        {
            title: 'Errors',
            dataIndex: 'errors',
            key: 'errors',
            render: (errors) => (
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {errors.map((error, index) => (
                        <li key={index} style={{ color: '#ff4d4f' }}>
                            {error}
                        </li>
                    ))}
                </ul>
            )
        }
    ];

    if (showResults && uploadResults) {
        return (
            <DashboardLayout>
                <div className="bulk-upload-lots-page">
                <PageHeader
                    title="Bulk Upload Results"
                    description="Results of your bulk lot upload"
                />
                
                <Result
                    status="success"
                    title="Upload Completed Successfully!"
                    subTitle={`Successfully created ${uploadResults.success_count} out of ${uploadResults.total_rows} lots`}
                    extra={[
                        <Button 
                            type="primary" 
                            key="view-lots"
                            onClick={() => navigate('/my-lots')}
                        >
                            View My Lots
                        </Button>,
                        <Button 
                            key="upload-more"
                            onClick={() => {
                                setShowResults(false);
                                setUploadResults(null);
                                // Restore form field from selectedAuction if it exists
                                if (selectedAuction) {
                                    setTimeout(() => {
                                        form.setFieldsValue({ auction_id: selectedAuction.id });
                                    }, 100);
                                }
                            }}
                        >
                            Upload More Lots
                        </Button>
                    ]}
                />

                {uploadResults.data && uploadResults.data.length > 0 && (
                    <Card title="Created Lots" style={{ marginTop: 24 }}>
                        <Table
                            dataSource={uploadResults.data}
                            columns={[
                                {
                                    title: 'Title',
                                    dataIndex: 'title',
                                    key: 'title'
                                },
                                {
                                    title: 'Starting Price',
                                    dataIndex: 'starting_price',
                                    key: 'starting_price',
                                    render: (price) => formatCurrency(price)
                                },
                                {
                                    title: 'Status',
                                    dataIndex: 'status',
                                    key: 'status',
                                    render: (status) => (
                                        <Tag color="green">{status.toUpperCase()}</Tag>
                                    )
                                }
                            ]}
                            pagination={false}
                            size="small"
                        />
                    </Card>
                )}
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="bulk-upload-lots-page">
            <PageHeader
                title="Bulk Upload Lots"
                description="Upload multiple lots at once using a CSV file"
                buttonText="Back to My Lots"
                buttonIcon={<ArrowLeftOutlined />}
                onButtonClick={() => navigate('/my-lots')}
            />

            <Spin spinning={loading}>
                {!currentUser?.has_auctions ? (
                <Alert
                    message="No Auctions Found"
                    description="You need to create an auction before you can upload lots."
                    type="warning"
                    showIcon
                    action={
                        <Button size="small" onClick={() => navigate('/auctions/create')}>
                            Create Auction
                        </Button>
                    }
                />
            ) : (
                <div className="bulk-upload-container" style={{ maxWidth: '1200px', margin: '0 auto' }}>
                    {/* Progress Steps Header */}
                    <Card 
                        style={{ 
                            marginBottom: 20,
                            background: '#f8f9fa',
                            border: '1px solid #e9ecef',
                            borderRadius: '8px'
                        }}
                        bodyStyle={{ padding: '20px' }}
                    >
                        <div style={{ textAlign: 'center' }}>
                            <Title level={3} style={{ marginBottom: 12, color: '#262626' }}>
                                Bulk Upload Process
                            </Title>
                            <Paragraph style={{ color: '#6c757d', fontSize: '14px', marginBottom: 16 }}>
                                Follow these simple steps to upload multiple lots at once
                            </Paragraph>
                            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
                                {[
                                    { step: 1, title: 'Download Template' },
                                    { step: 2, title: 'Select Auction' },
                                    { step: 3, title: 'Upload CSV' },
                                    { step: 4, title: 'Review & Upload' }
                                ].map((item, index) => (
                                    <div key={item.step} className="bulk-upload-step" style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        gap: '8px',
                                        padding: '8px 16px',
                                        background: 'white',
                                        borderRadius: '6px',
                                        border: '1px solid #dee2e6',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                                    }}>
                                        <div style={{ 
                                            width: '24px', 
                                            height: '24px', 
                                            borderRadius: '50%', 
                                            background: '#1890ff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '12px',
                                            fontWeight: 'bold',
                                            color: 'white'
                                        }}>
                                            {item.step}
                                        </div>
                                        <span style={{ color: '#262626', fontSize: '13px', fontWeight: 500 }}>
                                            {item.title}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </Card>

                    <Row gutter={[20, 20]}>
                        <Col xs={24} lg={16}>
                            {/* Step 1: Download Template */}
                            <Card 
                                style={{ 
                                    marginBottom: 16,
                                    border: '1px solid #e9ecef',
                                    borderRadius: '8px',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                                }}
                                bodyStyle={{ padding: '20px' }}
                            >
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ 
                                        width: '60px', 
                                        height: '60px', 
                                        borderRadius: '50%', 
                                        background: '#1890ff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 16px',
                                        boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)'
                                    }}>
                                        <DownloadOutlined style={{ fontSize: '24px', color: 'white' }} />
                                    </div>
                                    <Title level={5} style={{ marginBottom: 8, color: '#262626' }}>
                                        Step 1: Download Template
                                    </Title>
                                    <Paragraph style={{ color: '#6c757d', fontSize: '14px', marginBottom: 16 }}>
                                        Get our professionally designed CSV template with sample data to get started quickly
                                    </Paragraph>
                                    <Button 
                                        type="primary"
                                        icon={<DownloadOutlined />}
                                        onClick={downloadTemplate}
                                        style={{
                                            height: '36px',
                                            padding: '0 24px',
                                            borderRadius: '6px'
                                        }}
                                    >
                                        Download Template
                                    </Button>
                                </div>
                            </Card>

                            {/* Step 2: Select Auction */}
                            <Card 
                                style={{ 
                                    marginBottom: 16,
                                    border: selectedAuction ? '2px solid #52c41a' : '1px solid #e9ecef',
                                    borderRadius: '8px',
                                    boxShadow: selectedAuction ? '0 2px 8px rgba(82, 196, 26, 0.2)' : '0 1px 4px rgba(0,0,0,0.1)',
                                    background: selectedAuction ? '#f6ffed' : '#fff',
                                    transition: 'all 0.3s ease'
                                }}
                                bodyStyle={{ padding: '20px' }}
                            >
                                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                                    <div style={{ 
                                        width: '60px', 
                                        height: '60px', 
                                        borderRadius: '50%', 
                                        background: '#52c41a',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 16px',
                                        boxShadow: '0 2px 8px rgba(82, 196, 26, 0.3)'
                                    }}>
                                        <InfoCircleOutlined style={{ fontSize: '24px', color: 'white' }} />
                                    </div>
                                    <Title level={5} style={{ marginBottom: 8, color: '#262626' }}>
                                        Step 2: Select Auction
                                    </Title>
                                    <Paragraph style={{ color: '#6c757d', fontSize: '14px' }}>
                                        Choose which auction you want to add lots to
                                    </Paragraph>
                                    {selectedAuction && (
                                        <div style={{ 
                                            marginTop: '8px',
                                            padding: '8px 12px',
                                            background: '#52c41a',
                                            borderRadius: '6px',
                                            display: 'inline-block'
                                        }}>
                                            <span style={{ color: 'white', fontSize: '13px', fontWeight: 500 }}>
                                                ✓ Selected
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <Form form={form}>
                                    <Form.Item
                                        name="auction_id"
                                        rules={[{ required: true, message: 'Please select an auction' }]}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <Select
                                            placeholder="Select an auction"
                                            style={{ width: '100%' }}
                                            loading={loading}
                                            value={auctionIdValue}
                                            allowClear
                                            dropdownStyle={{ minWidth: '400px', maxWidth: '600px' }}
                                            dropdownMatchSelectWidth={false}
                                            optionLabelProp="label"
                                            onChange={(value) => {
                                                if (value) {
                                                    const auction = auctions.find(a => a.id === value);
                                                    setSelectedAuction(auction);
                                                    form.setFieldsValue({ auction_id: value });
                                                } else {
                                                    setSelectedAuction(null);
                                                    form.setFieldsValue({ auction_id: undefined });
                                                }
                                            }}
                                        >
                                            {auctions.map(auction => (
                                                <Option 
                                                    key={auction.id} 
                                                    value={auction.id}
                                                    label={auction.title.length > 70 ? `${auction.title.substring(0, 70)}...` : auction.title}
                                                >
                                                    <div style={{ 
                                                        display: 'flex', 
                                                        alignItems: 'flex-start', 
                                                        gap: '8px', 
                                                        width: '100%' 
                                                    }}>
                                                        <div style={{ 
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            lineHeight: '1.4',
                                                            flex: 1,
                                                            wordBreak: 'break-word'
                                                        }}>
                                                            {auction.title}
                                                        </div>
                                                        <Tag 
                                                            color={
                                                                auction.status === 'draft' ? 'default' :
                                                                auction.status === 'scheduled' ? 'orange' :
                                                                auction.status === 'active' ? 'blue' :
                                                                'green'
                                                            }
                                                            style={{ 
                                                                fontSize: '11px',
                                                                padding: '2px 6px',
                                                                flexShrink: 0,
                                                                marginTop: '2px'
                                                            }}
                                                        >
                                                            {auction.status.toUpperCase()}
                                                        </Tag>
                                                    </div>
                                                </Option>
                                            ))}
                                        </Select>
                                    </Form.Item>
                                </Form>
                            </Card>

                            {/* Step 3: Upload CSV */}
                            <Card 
                                style={{ 
                                    marginBottom: 16,
                                    border: '1px solid #e9ecef',
                                    borderRadius: '8px',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                                }}
                                bodyStyle={{ padding: '20px' }}
                            >
                                <div style={{ textAlign: 'center', marginBottom: 16 }}>
                                    <div style={{ 
                                        width: '60px', 
                                        height: '60px', 
                                        borderRadius: '50%', 
                                        background: '#fa8c16',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto 16px',
                                        boxShadow: '0 2px 8px rgba(250, 140, 22, 0.3)'
                                    }}>
                                        <UploadOutlined style={{ fontSize: '24px', color: 'white' }} />
                                    </div>
                                    <Title level={5} style={{ marginBottom: 8, color: '#262626' }}>
                                        Step 3: Upload CSV File
                                    </Title>
                                    <Paragraph style={{ color: '#6c757d', fontSize: '14px' }}>
                                        Upload your CSV file with lot data. Make sure it follows the template format
                                    </Paragraph>
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <Upload
                                        accept=".csv"
                                        showUploadList={false}
                                        beforeUpload={() => false}
                                        onChange={handleFileUpload}
                                    >
                                        <Button 
                                            type="primary"
                                            icon={<UploadOutlined />}
                                            style={{
                                                height: '36px',
                                                padding: '0 24px',
                                                borderRadius: '6px'
                                            }}
                                        >
                                            Choose CSV File
                                        </Button>
                                    </Upload>
                                </div>
                            </Card>

                        </Col>

                        <Col xs={24} lg={8}>
                            {/* CSV Format Guide */}
                            <Card 
                                title={
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <FileTextOutlined style={{ color: '#1890ff', fontSize: '16px' }} />
                                        <span style={{ fontSize: '14px' }}>CSV Format Guide</span>
                                    </div>
                                }
                                style={{ 
                                    marginBottom: 16,
                                    border: '1px solid #e9ecef',
                                    borderRadius: '8px',
                                    boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                                }}
                                bodyStyle={{ padding: '16px' }}
                            >
                                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                    <div>
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '6px',
                                            marginBottom: 8
                                        }}>
                                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '14px' }} />
                                            <span style={{ color: '#262626', fontSize: '13px', fontWeight: 600 }}>Required Fields</span>
                                        </div>
                                        <div style={{ 
                                            border: '1px solid #d9d9d9', 
                                            borderRadius: '6px', 
                                            padding: '12px' 
                                        }}>
                                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <code style={{ background: '#fff', padding: '1px 4px', fontSize: '11px', fontFamily: 'monospace' }}>title</code>
                                                    <span style={{ fontSize: '12px', color: '#666' }}>Lot title</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <code style={{ background: '#fff', padding: '1px 4px', fontSize: '11px', fontFamily: 'monospace' }}>description</code>
                                                    <span style={{ fontSize: '12px', color: '#666' }}>Lot description</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <code style={{ background: '#fff', padding: '1px 4px', fontSize: '11px', fontFamily: 'monospace' }}>starting_price</code>
                                                    <span style={{ fontSize: '12px', color: '#666' }}>Starting bid price</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <code style={{ background: '#fff', padding: '1px 4px', fontSize: '11px', fontFamily: 'monospace' }}>quantity</code>
                                                    <span style={{ fontSize: '12px', color: '#666' }}>Number of items</span>
                                                </div>
                                            </Space>
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '6px',
                                            marginBottom: 8
                                        }}>
                                            <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
                                            <span style={{ color: '#262626', fontSize: '13px', fontWeight: 600 }}>Optional Fields</span>
                                        </div>
                                        <div style={{ 
                                            border: '1px solid #d9d9d9', 
                                            borderRadius: '6px', 
                                            padding: '12px' 
                                        }}>
                                            <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <code style={{ background: '#fff', padding: '1px 4px', fontSize: '11px', fontFamily: 'monospace' }}>reserve_price</code>
                                                    <span style={{ fontSize: '12px', color: '#666' }}>Reserve price</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <code style={{ background: '#fff', padding: '1px 4px', fontSize: '11px', fontFamily: 'monospace' }}>default_increment</code>
                                                    <span style={{ fontSize: '12px', color: '#666' }}>Bid increment amount</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <code style={{ background: '#fff', padding: '1px 4px', fontSize: '11px', fontFamily: 'monospace' }}>video_url</code>
                                                    <span style={{ fontSize: '12px', color: '#666' }}>Video URL</span>
                                                </div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    <code style={{ background: '#fff', padding: '1px 4px', fontSize: '11px', fontFamily: 'monospace' }}>specifications</code>
                                                    <span style={{ fontSize: '12px', color: '#666' }}>Key:Value format</span>
                                                </div>
                                            </Space>
                                        </div>
                                    </div>

                                    <div style={{ 
                                        border: '1px solid #d9d9d9',
                                        borderRadius: '6px',
                                        padding: '12px',
                                        fontSize: '12px',
                                        marginBottom: 12
                                    }}>
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '6px',
                                            marginBottom: 8
                                        }}>
                                            <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
                                            <span style={{ color: '#262626', fontSize: '13px', fontWeight: 600 }}>Price Increment</span>
                                        </div>
                                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                            <div>
                                                <p style={{ fontSize: '12px', color: '#666', marginBottom: 4, margin: '0 0 4px 0' }}>
                                                    <strong>Description:</strong> Minimum amount by which bids must increase
                                                </p>
                                                <p style={{ fontSize: '12px', color: '#666', marginBottom: 4, margin: '0 0 4px 0' }}>
                                                    <strong>Default:</strong> If not specified, defaults to PKR 5
                                                </p>
                                                <p style={{ fontSize: '12px', color: '#666', marginBottom: 4, margin: '0 0 4px 0' }}>
                                                    <strong>Examples:</strong> 5, 10, 25, 50, 100
                                                </p>
                                            </div>
                                        </Space>
                                    </div>

                                    <div style={{ 
                                        border: '1px solid #d9d9d9',
                                        borderRadius: '6px',
                                        padding: '12px',
                                        fontSize: '12px'
                                    }}>
                                        <div style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            gap: '6px',
                                            marginBottom: 8
                                        }}>
                                            <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '14px' }} />
                                            <span style={{ color: '#262626', fontSize: '13px', fontWeight: 600 }}>Custom Specifications</span>
                                        </div>
                                        <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                            <div>
                                                <p style={{ fontSize: '12px', color: '#666', marginBottom: 4, margin: '0 0 4px 0' }}>
                                                    <strong>Simple Format:</strong> Group:Field:Value|Group:Field:Value
                                                </p>
                                                <code style={{ background: '#f5f5f5', padding: '2px 6px', fontSize: '11px', display: 'block', fontFamily: 'monospace' }}>
                                                    Physical:Material:Wood|Physical:Color:Brown|Technical:Processor:Intel i7
                                                </code>
                                            </div>
                                            <div>
                                                <p style={{ fontSize: '12px', color: '#666', marginBottom: 4, margin: '0 0 4px 0' }}>
                                                    <strong>Basic Format:</strong> Field:Value (no grouping)
                                                </p>
                                                <code style={{ background: '#f5f5f5', padding: '2px 6px', fontSize: '11px', display: 'block', fontFamily: 'monospace' }}>
                                                    Material:Wood|Color:Brown|Size:Large
                                                </code>
                                            </div>
                                            <div>
                                                <p style={{ fontSize: '12px', color: '#666', marginBottom: 4, margin: '0 0 4px 0' }}>
                                                    <strong>Examples:</strong>
                                                </p>
                                                <code style={{ background: '#f5f5f5', padding: '2px 6px', fontSize: '11px', display: 'block', fontFamily: 'monospace' }}>
                                                    Electronics:Brand:Samsung|Electronics:Model:Galaxy S21|Warranty:Period:2 Years
                                                </code>
                                            </div>
                                        </Space>
                                    </div>
                                </Space>
                            </Card>

                            {/* Validation Errors */}
                            {validationErrors.length > 0 && (
                                <Card 
                                    title={
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <ExclamationCircleOutlined style={{ color: '#ff4d4f', fontSize: '16px' }} />
                                            <span style={{ fontSize: '14px' }}>Validation Errors</span>
                                            <Tag color="red" style={{ marginLeft: 'auto', fontSize: '12px', padding: '1px 6px' }}>
                                                {validationErrors.length}
                                            </Tag>
                                        </div>
                                    }
                                    style={{ 
                                        border: '1px solid #ffccc7',
                                        borderRadius: '8px',
                                        boxShadow: '0 1px 4px rgba(255, 77, 79, 0.1)',
                                        background: '#fff2f0'
                                    }}
                                    bodyStyle={{ padding: '12px' }}
                                >
                                    <Table
                                        dataSource={validationErrors}
                                        columns={errorColumns}
                                        pagination={false}
                                        size="small"
                                        scroll={{ y: 200 }}
                                        style={{ 
                                            background: '#fff2f0',
                                            borderRadius: '6px'
                                        }}
                                    />
                                </Card>
                            )}
                        </Col>
                    </Row>
                </div>
            )}

            {csvData.length > 0 && (
                <Card 
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '16px' }} />
                            <span style={{ fontSize: '14px' }}>Step 4: Review & Upload</span>
                        </div>
                    }
                    style={{ 
                        marginTop: 16,
                        border: '1px solid #e9ecef',
                        borderRadius: '8px',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                    }}
                    bodyStyle={{ padding: '16px' }}
                >
                    <div style={{ marginBottom: 12 }}>
                        <Tag color="blue" style={{ fontSize: '12px', padding: '2px 8px' }}>
                            {csvData.length} lots loaded
                        </Tag>
                        {validationErrors.length > 0 && (
                            <Tag color="red" style={{ fontSize: '12px', padding: '2px 8px', marginLeft: 8 }}>
                                {validationErrors.length} errors found
                            </Tag>
                        )}
                    </div>
                    <Table
                        dataSource={csvData}
                        columns={csvColumns}
                        pagination={{ pageSize: 10 }}
                        size="small"
                        scroll={{ x: 800 }}
                        style={{ 
                            background: '#fafafa',
                            borderRadius: '6px',
                            marginBottom: 16
                        }}
                    />
                    <div style={{ textAlign: 'center' }}>
                        <Space size="middle">
                            <Button
                                type="primary"
                                onClick={handleUpload}
                                loading={uploading}
                                disabled={validationErrors.length > 0}
                                icon={<UploadOutlined />}
                                style={{
                                    height: '36px',
                                    padding: '0 24px',
                                    borderRadius: '6px'
                                }}
                            >
                                Upload Lots
                            </Button>
                            <Button
                                onClick={() => {
                                    setCsvData([]);
                                    setValidationErrors([]);
                                }}
                                icon={<DeleteOutlined />}
                                style={{
                                    height: '36px',
                                    padding: '0 24px',
                                    borderRadius: '6px'
                                }}
                            >
                                Clear Data
                            </Button>
                        </Space>
                    </div>
                </Card>
            )}
            </Spin>
            </div>
            <style>{`
                .bulk-upload-lots-page {
                    width: 100%;
                    padding: 0;
                }
                
                .bulk-upload-lots-page .bulk-upload-container {
                    width: 100%;
                    padding: 0 16px;
                    box-sizing: border-box;
                }
                
                .bulk-upload-lots-page .ant-card-body {
                    padding: 20px;
                }
                
                .bulk-upload-lots-page .ant-row {
                    margin-left: 0 !important;
                    margin-right: 0 !important;
                }
                
                .bulk-upload-lots-page .ant-col {
                    padding-left: 10px !important;
                    padding-right: 10px !important;
                }
                
                @media (max-width: 1199px) {
                    .bulk-upload-lots-page .bulk-upload-container {
                        padding: 0 12px;
                    }
                }
                
                @media (max-width: 991px) {
                    .bulk-upload-lots-page .bulk-upload-container {
                        padding: 0 12px;
                    }
                    
                    .bulk-upload-lots-page .ant-card-body {
                        padding: 16px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-card {
                        margin-bottom: 12px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-table-wrapper {
                        overflow-x: auto;
                    }
                    
                    .bulk-upload-lots-page .ant-table {
                        min-width: 600px;
                    }
                    
                    .bulk-upload-lots-page .ant-col {
                        padding-left: 8px !important;
                        padding-right: 8px !important;
                    }
                }
                
                @media (max-width: 767px) {
                    .bulk-upload-lots-page .bulk-upload-container {
                        padding: 0 8px;
                    }
                    
                    .bulk-upload-lots-page .ant-card-body {
                        padding: 12px !important;
                    }
                    
                    .bulk-upload-lots-page h3.ant-typography,
                    .bulk-upload-lots-page h3.ant-typography * {
                        font-size: 18px !important;
                        line-height: 1.4 !important;
                    }
                    
                    .bulk-upload-lots-page h5.ant-typography,
                    .bulk-upload-lots-page h5.ant-typography * {
                        font-size: 14px !important;
                        line-height: 1.4 !important;
                    }
                    
                    .bulk-upload-lots-page .ant-typography,
                    .bulk-upload-lots-page .ant-typography * {
                        font-size: 13px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-typography.ant-typography-secondary {
                        font-size: 12px !important;
                    }
                    
                    .bulk-upload-lots-page .bulk-upload-step {
                        padding: 6px 10px !important;
                        font-size: 11px !important;
                        flex: 1 1 calc(50% - 8px) !important;
                        min-width: calc(50% - 8px) !important;
                        max-width: calc(50% - 8px) !important;
                    }
                    
                    .bulk-upload-lots-page .bulk-upload-step span {
                        font-size: 11px !important;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }
                    
                    .bulk-upload-lots-page .bulk-upload-step > div:first-child {
                        width: 20px !important;
                        height: 20px !important;
                        font-size: 10px !important;
                        flex-shrink: 0 !important;
                    }
                    
                    .bulk-upload-lots-page .ant-btn,
                    .bulk-upload-lots-page button.ant-btn {
                        width: 100% !important;
                        margin-bottom: 8px !important;
                        height: auto !important;
                        min-height: 36px !important;
                        padding: 8px 16px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-btn-icon {
                        font-size: 14px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-space {
                        width: 100% !important;
                        display: flex !important;
                        flex-direction: column !important;
                    }
                    
                    .bulk-upload-lots-page .ant-space-item {
                        width: 100% !important;
                    }
                    
                    .bulk-upload-lots-page .ant-upload,
                    .bulk-upload-lots-page .ant-upload-btn {
                        width: 100% !important;
                    }
                    
                    .bulk-upload-lots-page .ant-select,
                    .bulk-upload-lots-page .ant-select-selector {
                        width: 100% !important;
                    }
                    
                    .bulk-upload-lots-page .ant-table-wrapper {
                        overflow-x: auto;
                        -webkit-overflow-scrolling: touch;
                        margin: 0 -12px;
                        padding: 0 12px;
                    }
                    
                    .bulk-upload-lots-page .ant-table {
                        font-size: 12px !important;
                        min-width: 700px;
                    }
                    
                    .bulk-upload-lots-page .ant-table-thead > tr > th {
                        padding: 8px 4px !important;
                        font-size: 11px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-table-tbody > tr > td {
                        padding: 8px 4px !important;
                        font-size: 11px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-descriptions-item-label {
                        font-size: 11px !important;
                        padding: 4px 8px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-descriptions-item-content {
                        font-size: 11px !important;
                        padding: 4px 8px !important;
                    }
                    
                    .bulk-upload-lots-page code {
                        font-size: 10px !important;
                        word-break: break-all;
                        display: inline-block;
                        max-width: 100%;
                    }
                    
                    .bulk-upload-lots-page .ant-tag {
                        font-size: 11px !important;
                        padding: 2px 6px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-card-head-title {
                        font-size: 13px !important;
                    }
                    
                    .bulk-upload-lots-page [class*="anticon"] {
                        font-size: 16px !important;
                    }
                    
                    .bulk-upload-lots-page div[style*="width: '60px'"] {
                        width: 50px !important;
                        height: 50px !important;
                    }
                    
                    .bulk-upload-lots-page div[style*="width: '60px'"] [class*="anticon"] {
                        font-size: 20px !important;
                    }
                }
                
                @media (max-width: 575px) {
                    .bulk-upload-lots-page .bulk-upload-container {
                        padding: 0 4px;
                    }
                    
                    .bulk-upload-lots-page .ant-col {
                        padding-left: 4px !important;
                        padding-right: 4px !important;
                    }
                }
                
                @media (max-width: 480px) {
                    .bulk-upload-lots-page .bulk-upload-container {
                        padding: 0 4px;
                    }
                    
                    .bulk-upload-lots-page .ant-card-body {
                        padding: 10px !important;
                    }
                    
                    .bulk-upload-lots-page h3.ant-typography,
                    .bulk-upload-lots-page h3.ant-typography * {
                        font-size: 16px !important;
                        line-height: 1.3 !important;
                    }
                    
                    .bulk-upload-lots-page h5.ant-typography,
                    .bulk-upload-lots-page h5.ant-typography * {
                        font-size: 13px !important;
                        line-height: 1.3 !important;
                    }
                    
                    .bulk-upload-lots-page .ant-typography,
                    .bulk-upload-lots-page .ant-typography * {
                        font-size: 12px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-typography.ant-typography-secondary {
                        font-size: 11px !important;
                    }
                    
                    .bulk-upload-lots-page .bulk-upload-step {
                        padding: 5px 8px !important;
                        font-size: 10px !important;
                        flex: 1 1 100% !important;
                        min-width: 100% !important;
                        max-width: 100% !important;
                        margin-bottom: 8px !important;
                    }
                    
                    .bulk-upload-lots-page .bulk-upload-step span {
                        font-size: 10px !important;
                    }
                    
                    .bulk-upload-lots-page .bulk-upload-step > div:first-child {
                        width: 18px !important;
                        height: 18px !important;
                        font-size: 9px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-table {
                        font-size: 11px !important;
                        min-width: 600px;
                    }
                    
                    .bulk-upload-lots-page .ant-table-thead > tr > th {
                        padding: 6px 3px !important;
                        font-size: 10px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-table-tbody > tr > td {
                        padding: 6px 3px !important;
                        font-size: 10px !important;
                    }
                    
                    .bulk-upload-lots-page code {
                        font-size: 9px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-tag {
                        font-size: 10px !important;
                        padding: 1px 4px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-space-item {
                        margin-bottom: 4px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-card-head-title {
                        font-size: 12px !important;
                    }
                    
                    .bulk-upload-lots-page [class*="anticon"] {
                        font-size: 14px !important;
                    }
                    
                    .bulk-upload-lots-page div[style*="width: '60px'"] {
                        width: 40px !important;
                        height: 40px !important;
                    }
                    
                    .bulk-upload-lots-page div[style*="width: '60px'"] [class*="anticon"] {
                        font-size: 18px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-btn,
                    .bulk-upload-lots-page button.ant-btn {
                        min-height: 32px !important;
                        padding: 6px 12px !important;
                        font-size: 13px !important;
                    }
                }
                
                @media (max-width: 360px) {
                    .bulk-upload-lots-page .ant-card-body {
                        padding: 8px !important;
                    }
                    
                    .bulk-upload-lots-page h3.ant-typography,
                    .bulk-upload-lots-page h3.ant-typography * {
                        font-size: 14px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-table {
                        font-size: 10px !important;
                        min-width: 550px;
                    }
                    
                    .bulk-upload-lots-page .ant-table-thead > tr > th {
                        padding: 4px 2px !important;
                        font-size: 9px !important;
                    }
                    
                    .bulk-upload-lots-page .ant-table-tbody > tr > td {
                        padding: 4px 2px !important;
                        font-size: 9px !important;
                    }
                }
            `}</style>
        </DashboardLayout>
    );
};

export default BulkUploadLots;
